/**
 * Native App Update Manager
 *
 * Controlla e gestisce gli aggiornamenti delle app native negli store.
 *
 * iOS     → confronto versione tramite endpoint server + modal custom (NativeUpdateModal.tsx)
 * Android → confronto versionCode tramite endpoint server + stesso modal custom,
 *            pulsante "Aggiorna ora" apre il Play Store
 *
 * Principi:
 * - Non-bloccante: sempre try/catch, ritorna null in caso di errore
 * - Platform-gated: no-op su web
 * - Throttled: controlla al max ogni 6 ore per sessione
 */

import { Capacitor } from '@capacitor/core';
import { logger } from '@deskbay/shared';

export interface NativeUpdateResult {
  updateAvailable: boolean;
  forceUpdate: boolean;
  storeUrl?: string;
}

interface MobileVersionConfig {
  ios: {
    latestVersion: string;
    minRequiredVersion: string;
    forceUpdate: boolean;
    storeUrl: string;
  };
  android: {
    latestVersionCode: number;
    minRequiredVersionCode: number;
    forceUpdate: boolean;
    storeUrl?: string;
  };
}

// URL assoluto obbligatorio: Capacitor intercetta le richieste relative a app.deskbay.io
// (WebViewAssetLoader su Android, URL scheme handler su iOS) e le servirebbe dal bundle locale,
// mostrando sempre la versione corrente come "latest". CapacitorHttp bypassa questo meccanismo
// eseguendo una vera richiesta HTTP nativa fuori dal contesto del WebView.
const API_URL = 'https://app.deskbay.io/api/mobile-version.json';
const CHECK_INTERVAL_MS = 6 * 60 * 60 * 1000; // 6 ore

let lastCheckTs = 0;

/**
 * Controlla se è disponibile un aggiornamento nativo.
 *
 * - Android: gestisce l'aggiornamento autonomamente tramite Google Play API.
 * - iOS: ritorna lo stato per permettere al NativeUpdateModal di mostrare il prompt.
 *
 * Usa CapacitorHttp (@capacitor/core) invece di fetch() per bypassare il WebViewAssetLoader
 * e garantire che la risposta provenga sempre dal server remoto.
 *
 * Ritorna null se: web, throttle attivo, errore di rete.
 */
export async function checkNativeUpdate(): Promise<NativeUpdateResult | null> {
  if (!Capacitor.isNativePlatform()) return null;

  // Throttle: non controllare più di una volta ogni 6 ore per sessione
  if (Date.now() - lastCheckTs < CHECK_INTERVAL_MS) return null;
  lastCheckTs = Date.now();

  try {
    const { CapacitorHttp } = await import('@capacitor/core');
    const response = await CapacitorHttp.get({
      url: `${API_URL}?t=${Date.now()}`,
    });

    if (response.status !== 200 || !response.data) return null;

    const config = response.data as MobileVersionConfig;
    const nativePlatform = Capacitor.getPlatform();

    if (nativePlatform === 'android') return await checkAndroidUpdate(config.android);
    if (nativePlatform === 'ios')     return await checkIOSUpdate(config.ios);

    return null;
  } catch (err) {
    logger.capacitor.warn('Native update check failed (non-fatal)', 'updateManager', { error: err });
    return null;
  }
}

async function checkAndroidUpdate(
  config: MobileVersionConfig['android']
): Promise<NativeUpdateResult | null> {
  try {
    const { App } = await import('@capacitor/app');
    const appInfo = await App.getInfo();
    const currentCode = Number(appInfo.build ?? 0);

    const isOutdated = currentCode < config.latestVersionCode;
    if (!isOutdated) {
      return { updateAvailable: false, forceUpdate: false };
    }

    const forceUpdate = config.forceUpdate || currentCode < config.minRequiredVersionCode;

    logger.capacitor.info('Android update available', 'updateManager', {
      currentCode,
      latestVersionCode: config.latestVersionCode,
      forceUpdate,
    });

    return { updateAvailable: true, forceUpdate, storeUrl: config.storeUrl };
  } catch (err) {
    logger.capacitor.warn('Android update check failed (non-fatal)', 'updateManager', { error: err });
    return null;
  }
}

async function checkIOSUpdate(
  config: MobileVersionConfig['ios']
): Promise<NativeUpdateResult | null> {
  try {
    const { App } = await import('@capacitor/app');
    const appInfo = await App.getInfo();
    const currentVersion = appInfo.version;

    const isOutdated = compareVersions(config.latestVersion, currentVersion) > 0;
    if (!isOutdated) {
      return { updateAvailable: false, forceUpdate: false, storeUrl: config.storeUrl };
    }

    const isBelowMin = compareVersions(currentVersion, config.minRequiredVersion) < 0;
    const forceUpdate = config.forceUpdate || isBelowMin;

    logger.capacitor.info('iOS update available', 'updateManager', {
      currentVersion,
      latestVersion: config.latestVersion,
      forceUpdate,
    });

    return { updateAvailable: true, forceUpdate, storeUrl: config.storeUrl };
  } catch (err) {
    logger.capacitor.warn('iOS update check failed (non-fatal)', 'updateManager', { error: err });
    return null;
  }
}

/**
 * Confronta due versioni semantiche (major.minor.patch).
 * Ritorna > 0 se a > b, < 0 se a < b, 0 se uguali.
 */
function compareVersions(a: string, b: string): number {
  const pa = a.split('.').map(Number);
  const pb = b.split('.').map(Number);
  for (let i = 0; i < 3; i++) {
    const diff = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
}
